import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import 'odometer/themes/odometer-theme-default.css';
import { Box, Typography } from '@mui/material';
import Odometer from 'react-odometerjs';
import { useTranslation } from 'react-i18next';
import { AccessTimeFilled } from '@mui/icons-material';

import { useLocationsCacheContext } from '../../../../../../../context/LocationsCacheContext';
import { formatOdometer } from '../OdometerDisplay';
import CorGPSDisplay from '../CorGPSDisplay';
import CustomTooltip from '../../../../../../shared/atoms/CustomTooltip';
import CopyCoordinatesIconButton from '../../../../../../shared/molecules/CopyCoordinatesIconButton';
import MiniMap from '../../../../../../shared/organisms/MiniMap';

const PositioningDisplay = ({ sensor, simplified }) => {
  const { t, i18n } = useTranslation();
  const { assets } = useLocationsCacheContext();

  const asset = useMemo(
    () => assets.find(a => a.properties.sensors.some(s => s.id === sensor.id)),
    [assets, sensor],
  );

  const sensorMarkers = [
    {
      geometry: {
        type: 'Point',
        coordinates: [
          Number.parseFloat(sensor.position?.lng),
          Number.parseFloat(sensor.position?.lat),
        ],
      },
      properties: {
        ...sensor,
        icon: asset?.properties?.icon || 'general.svg',
      },
    },
  ];

  if (!sensor.position)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        width="100%"
        gap={1}
      >
        <Typography variant="h6">
          {t('machines.facilities.center_pane.sensor.no_position')}
        </Typography>
      </Box>
    );

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      gap={1}
    >
      <Box height="100%" flexGrow={1}>
        <MiniMap
          centerPosition={[
            sensor.position?.lng || 0,
            sensor.position?.lat || 0,
          ]}
          features={sensorMarkers}
          selectedMarker={sensor}
          simplified={simplified}
        />
      </Box>

      {!simplified && (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={1}
          justifyContent="end"
          alignItems="center"
          fontSize="1.2rem"
          fontWeight="500"
        >
          <CustomTooltip
            title={t(
              'machines.facilities.center_pane.sensor.positioning_coordinates',
              {
                longitude: sensor.position?.lng
                  ? Number.parseFloat(sensor.position?.lng).toFixed(4)
                  : '',
                latitude: sensor.position?.lat
                  ? Number.parseFloat(sensor.position?.lat).toFixed(4)
                  : '',
              },
            )}
          >
            <Box
              display="flex"
              alignItems="center"
              sx={{
                whiteSpace: 'nowrap',
                '& > .odometer': {
                  lineHeight: 'unset !important',
                  fontFamily: 'unset !important',
                },
              }}
            >
              <Odometer
                format={formatOdometer(sensor.reading, i18n.language, 4)}
                duration={100}
                value={sensor.position?.lat}
              />
              ; &nbsp;
              <Odometer
                format={formatOdometer(sensor.reading, i18n.language, 4)}
                duration={100}
                value={sensor.position?.lng}
              />
            </Box>
          </CustomTooltip>

          <CopyCoordinatesIconButton
            lat={sensor.position?.lat}
            lng={sensor.position?.lng}
          />

          {sensor.reading_date && (
            <CustomTooltip
              title={new Date(sensor.reading_date).toLocaleString(
                i18n.language,
              )}
              style={{ zIndex: 2 }}
            >
              <AccessTimeFilled />
            </CustomTooltip>
          )}

          {(sensor.data?.lsrc != null ||
            sensor.data?.estBattPct != null ||
            sensor.data?.hacc != null ||
            sensor.data?.pdop != null) && (
            <Box display="flex" gap={1} alignItems="center">
              <CorGPSDisplay sensor={sensor} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

PositioningDisplay.propTypes = {
  simplified: PropTypes.bool,
  sensor: PropTypes.shape({
    id: PropTypes.string,
    position: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    reading: PropTypes.number,
    unit: PropTypes.string,
    reading_date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.shape({
      lsrc: PropTypes.string,
      estBattPct: PropTypes.number,
      hacc: PropTypes.number,
      pdop: PropTypes.number,
    }),
  }),
};

export default PositioningDisplay;
